import { AlertTriangle, ArrowRight, CalendarPlus, PackageCheck, Server, TriangleAlert } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { DataTable, Paginacao, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { Stat, StatRow } from '@/components/patterns/metric';
import { EstadoErro } from '@/components/patterns/states';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { InstalacaoFlags, SaudeBadge, StatusAgendamentoBadge } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import { dataSource } from '@/data';
import type { Agendamento, EventoHistorico, Instalacao } from '@/data/types';
import { usePaginacao } from '@/hooks/use-paginacao';
import { useResource } from '@/hooks/use-resource';
import { formatarData, plural, texto } from '@/lib/format';
import { cn } from '@/lib/utils';
import { estaDesatualizada, ordenarPorFamilia, proximosAgendamentos, resumirEscopo } from '@/lib/rules';

/**
 * Visão geral.
 *
 * A tela do app original empilhava quatro caixas de escopo cujo conteúdo vinha
 * pronto do backend (`total_escopo`, `texto_escopo`, `saude_escopo`) e duas
 * grades. Aqui os indicadores são derivados do próprio escopo carregado — e por
 * isso conseguem dizer *quantas* instalações estão desatualizadas, não só
 * repetir um texto. Onde o backend é a única fonte possível (a frase que
 * descreve o modo de operação), o texto dele continua sendo exibido.
 */
export function HomePage() {
  const carregar = useCallback(async (signal: AbortSignal) => {
    const [instalacoes, agendamentos, historico] = await Promise.all([
      dataSource.listInstalacoes(signal),
      dataSource.listAgendamentos(signal),
      dataSource.listHistorico(signal),
    ]);
    return { instalacoes, agendamentos, historico };
  }, []);

  const recurso = useResource<{ instalacoes: Instalacao[]; agendamentos: Agendamento[]; historico: EventoHistorico[] }>(carregar);

  const instalacoes = useMemo(
    () => ordenarPorFamilia(recurso.dados?.instalacoes ?? []),
    [recurso.dados],
  );
  const agendamentos = useMemo(() => recurso.dados?.agendamentos ?? [], [recurso.dados]);
  const historico = useMemo(() => recurso.dados?.historico ?? [], [recurso.dados]);

  const paginacaoInstalacoes = usePaginacao(instalacoes, '', 10);

  const resumo = resumirEscopo(instalacoes, agendamentos, historico);
  const proximos = proximosAgendamentos(agendamentos);
  const familias = new Set(instalacoes.map((item) => item.controladoraId ?? item.id)).size;

  const cabecalho = (
    <PageHeader
      eyebrow="Painel do cliente"
      titulo="Visão geral"
      descricao="Todas as instalações que você acessa, de todas as famílias — cada Controlada aparece aninhada logo abaixo da sua Controladora."
      /* Hard rule 1 — uma única ação primária nesta tela. */
      acao={
        <Button variant="primary" asChild>
          <Link to="/agendar">
            <CalendarPlus aria-hidden="true" />
            Agendar atualização
          </Link>
        </Button>
      }
    />
  );

  if (recurso.estado === 'erro') {
    return (
      <PageBody>
        {cabecalho}
        <TabelaPainel>
          <EstadoErro erro={recurso.erro} aoTentarDeNovo={recurso.recarregar} />
        </TabelaPainel>
      </PageBody>
    );
  }

  const carregando = recurso.estado === 'carregando';

  const colunasInstalacoes: Array<Coluna<Instalacao>> = [
    {
      id: 'nome',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.nome,
      celula: (item) => (
        <div className={cn('flex flex-col gap-0.5', item.controladoraId != null && 'pl-4')}>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-bold">{item.nome}</span>
            <InstalacaoFlags ana={item.ana} interno={item.interno} />
          </span>
          <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
            {texto(item.cliente?.nome, 'Cliente sem dado')}
          </span>
        </div>
      ),
    },
    {
      id: 'versao',
      cabecalho: 'Versão',
      largura: '170px',
      ordenarPor: (item) => item.versaoAtual,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versaoAtual)}</span>
          {estaDesatualizada(item) ? (
            <span className="text-micro font-normal uppercase tracking-[0.04em] text-warning-text">
              recomendada {item.versaoEstavelRecomendada}
            </span>
          ) : (
            <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">em dia</span>
          )}
        </div>
      ),
    },
    { id: 'saude', cabecalho: 'Saúde', largura: '130px', celula: (item) => <SaudeBadge valor={item.saude} /> },
  ];

  const colunasAgendamentos: Array<Coluna<Agendamento>> = [
    {
      id: 'instalacao',
      cabecalho: 'Instalação',
      celula: (item) => <span className="font-bold">{texto(item.instalacao)}</span>,
    },
    {
      id: 'quando',
      cabecalho: 'Quando',
      largura: '150px',
      ordenarPor: (item) => `${item.data ?? ''}${item.hora}`,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="tabular-nums">{formatarData(item.data)}</span>
          <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
            às {texto(item.hora)}
          </span>
        </div>
      ),
    },
    {
      id: 'versao',
      cabecalho: 'Versão',
      largura: '110px',
      celula: (item) => <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versao)}</span>,
    },
    { id: 'status', cabecalho: 'Status', largura: '150px', celula: (item) => <StatusAgendamentoBadge valor={item.status} /> },
  ];

  return (
    <PageBody>
      {cabecalho}

      {carregando ? (
        <Skeleton className="h-[104px] rounded-panel" />
      ) : (
        <Card raio="panel">
          <StatRow>
            <Stat
              rotulo="Instalações no escopo"
              valor={resumo.total}
              icone={<Server className="size-3.5" aria-hidden="true" />}
              descricao={`Em ${plural(familias, 'família', 'famílias')} (Controladora + Controladas, ou avulsa).`}
            />
            <Stat
              rotulo="Desatualizadas"
              valor={resumo.desatualizadas}
              icone={<PackageCheck className="size-3.5" aria-hidden="true" />}
              descricao={
                resumo.desatualizadas === 0
                  ? 'Todo o escopo na versão recomendada.'
                  : `De ${plural(resumo.total, 'instalação', 'instalações')} no escopo.`
              }
            />
            <Stat
              rotulo="Saúde do escopo"
              valor={resumo.emIncidente + resumo.emAtencao}
              icone={<TriangleAlert className="size-3.5" aria-hidden="true" />}
              descricao={
                resumo.emIncidente + resumo.emAtencao === 0
                  ? 'Nenhum incidente aberto.'
                  : `${plural(resumo.emIncidente, 'incidente', 'incidentes')} e ${resumo.emAtencao} em atenção.`
              }
            />
            <Stat
              rotulo="Atualizações programadas"
              valor={resumo.agendamentosProgramados}
              icone={<AlertTriangle className="size-3.5" aria-hidden="true" />}
              descricao={
                proximos.length > 0
                  ? `Próxima em ${formatarData(proximos[0].data)}, às ${texto(proximos[0].hora)}.`
                  : 'Nada programado no momento.'
              }
            />
          </StatRow>
        </Card>
      )}

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <TabelaPainel
          titulo="Instalações no escopo"
          contagem={carregando ? undefined : plural(instalacoes.length, 'instalação', 'instalações')}
          paginacao={
            recurso.estado === 'pronto' ? (
              <Paginacao
                pagina={paginacaoInstalacoes.pagina}
                totalPaginas={paginacaoInstalacoes.totalPaginas}
                aoMudar={paginacaoInstalacoes.setPagina}
                tamanhoPagina={paginacaoInstalacoes.tamanhoPagina}
                aoMudarTamanho={paginacaoInstalacoes.setTamanhoPagina}
                tamanhosPagina={[10, 25, 50]}
              />
            ) : undefined
          }
        >
          <DataTable
            rotulo="Instalações no escopo"
            itens={paginacaoInstalacoes.visiveis}
            colunas={colunasInstalacoes}
            chave={(item) => item.id}
            estado={recurso.estado}
            erro={recurso.erro}
            aoTentarDeNovo={recurso.recarregar}
            vazio={{
              titulo: 'Nenhuma instalação no escopo',
              descricao: 'A instalação de acesso deste usuário não tem instalações vinculadas.',
            }}
          />
        </TabelaPainel>

        <TabelaPainel
          titulo="Próximos agendamentos"
          acao={
            <Button variant="link" size="sm" asChild>
              <Link to="/agendamentos">
                Ver todos
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          }
        >
          <DataTable
            rotulo="Próximos agendamentos"
            itens={proximos}
            colunas={colunasAgendamentos}
            chave={(item) => item.id}
            estado={recurso.estado}
            erro={recurso.erro}
            aoTentarDeNovo={recurso.recarregar}
            vazio={{
              titulo: 'Nada programado',
              descricao: 'Não há atualizações agendadas para as instalações do seu escopo.',
              acao: (
                <Button variant="outline" asChild>
                  <Link to="/agendar">Agendar a primeira</Link>
                </Button>
              ),
            }}
          />
        </TabelaPainel>
      </div>
    </PageBody>
  );
}
